import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout.jsx";
import axiosInstance from "../services/axiosInstance";
import TokenManager from "../services/tokenManager";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./Client.css";

const TravelAllowanceForm = () => {
  const navigate = useNavigate();
  const formRef = useRef();

  // --------------------- State Management ---------------------
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");

  // Header fields (employee details)
  const [formData, setFormData] = useState({
    employeeName: "",
    monthYear: "",
    projectName: "",            // new field
    costCenter: "",
    designation: "",
    supportingTA: "",
    payment: "",
    location: "",
  });

  // Expense rows (line items)
  const [rows, setRows] = useState([]);

  // Helper for notifications
  const showNotificationMessage = (type, message) => {
    setNotificationType(type);
    setNotificationMessage(message);
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  // Handle header field changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Add a new empty expense row
  const addRow = () => {
    const newId = rows.length ? Math.max(...rows.map((r) => r.id)) + 1 : 1;
    setRows([
      ...rows,
      {
        id: newId,
        date: "",
        costCenter: "",
        particulars: "",
        mode: "Cash",
        amount: "",
        ticketImage: null,
      },
    ]);
  };

  // Delete a row
  const deleteRow = (id) => {
    setRows(rows.filter((row) => row.id !== id));
  };

  // Update a row field
  const updateRow = (id, field, value) => {
    setRows(
      rows.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  // Handle ticket image upload
  const handleTicketImageUpload = (id, file) => {
    if (file) {
      updateRow(id, "ticketImage", file);
    }
  };

  // Validation
  const validateForm = () => {
    const errors = {};
    if (!formData.employeeName?.trim()) errors.employeeName = "Employee name is required";
    if (!formData.monthYear?.trim()) errors.monthYear = "Month/Year is required";
    if (!formData.designation?.trim()) errors.designation = "Designation is required";
    if (rows.length === 0) {
      errors.rows = "At least one expense row is required";
    } else {
      rows.forEach((row, index) => {
        if (!row.date?.trim()) errors[`row_${row.id}_date`] = `Row ${index + 1}: Date required`;
        if (!row.amount || isNaN(parseFloat(row.amount)) || parseFloat(row.amount) <= 0)
          errors[`row_${row.id}_amount`] = `Row ${index + 1}: Valid amount required`;
      });
    }
    return errors;
  };

  // Format YYYY-MM-DD to DD.MM.YY
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}.${month}.${year.slice(-2)}`;
  };

  // --------------------- EXCEL GENERATION ---------------------
  // Note: Excel generation is handled on the backend when form is submitted
  const downloadExcel = () => {
    showNotificationMessage("info", "Submit the form to receive Excel file via email");
  };

  // --------------------- PDF GENERATION (kept as before, but can be enhanced similarly if needed) ---------------------
  const downloadPDF = () => {
    const doc = new jsPDF();

    doc.setFillColor(245, 245, 245);
    doc.rect(10, 10, 190, 25, "F");

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("LIGHTFORCE BUILDINT PRIVATE LIMITED", 105, 18, { align: "center" });

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text("408-412, srishti palza, off. saik vihar road powai, mumbai 400076", 105, 24, { align: "center" });

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Conveyance & Other Expenses", 105, 38, { align: "center" });

    const empDetails = [
      ["Name", formData.employeeName, "Month/Year", formData.monthYear],
      ["Cost Center", formData.costCenter, "Designation", formData.designation],
      ["Project Name", formData.projectName, "Supporting TA", formData.supportingTA],
      ["Location", formData.location, "Payment", formData.payment],
    ];

    autoTable(doc, {
      startY: 45,
      head: [],
      body: empDetails,
      theme: "plain",
      styles: { fontSize: 9, cellPadding: 2 },
      columnStyles: { 0: { fontStyle: "bold", cellWidth: 30 }, 1: { cellWidth: 50 }, 2: { fontStyle: "bold", cellWidth: 30 }, 3: { cellWidth: 50 } },
      margin: { left: 14, right: 14 },
      tableLineColor: [200, 200, 200],
      tableLineWidth: 0.1,
    });

    const tableBody = rows.map((row) => [
      row.id,
      formatDate(row.date),
      row.costCenter,
      row.particulars,
      row.mode,
      `₹ ${parseFloat(row.amount || 0).toFixed(2)}`,
    ]);

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [["#", "Date", "Cost Center", "Particulars", "Payment", "Amount (₹)"]],
      body: tableBody,
      theme: "grid",
      headStyles: { fillColor: [70, 130, 180], textColor: 255, fontStyle: "bold", fontSize: 9 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      styles: { fontSize: 8, cellPadding: 3, lineColor: [200, 200, 200], lineWidth: 0.1 },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 20 },
        2: { cellWidth: 20 },
        3: { cellWidth: "auto" },
        4: { cellWidth: 18 },
        5: { cellWidth: 22, halign: "right" },
      },
      margin: { left: 14, right: 14 },
    });

    const total = rows.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
    if (total > 0) {
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text(`Total Amount: ₹ ${total.toFixed(2)}`, 190, doc.lastAutoTable.finalY + 6, { align: "right" });
    }

    // Signature lines
    const signatureY = doc.lastAutoTable.finalY + (total > 0 ? 15 : 10);
    doc.setFontSize(9);
    doc.setFont("helvetica", "italic");
    doc.text("Receiver signature", 20, signatureY);
    doc.text("Project Manager", 80, signatureY);
    doc.text("Md Office", 140, signatureY);
    doc.text("Finance Signature", 20, signatureY + 8);
    doc.text("Director Signature", 80, signatureY + 8);

    doc.save("TravelAllowance.pdf");
  };

  // --------------------- SUBMIT TO BACKEND (unchanged) ---------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      showNotificationMessage("error", "Please fix the validation errors");
      return;
    }

    const token = TokenManager.getAccessToken();
    if (!token) {
      showNotificationMessage("error", "Session expired. Please login again.");
      setTimeout(() => navigate("/login"), 2000);
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key]) formDataToSend.append(key, formData[key]);
      });

      const rowsForJSON = rows.map(({ id, date, costCenter, particulars, mode, amount }) => ({
        date,
        costCenter,
        particulars,
        mode,
        amount,
      }));
      formDataToSend.append("rows", JSON.stringify(rowsForJSON));

      rows.forEach((row, index) => {
        if (row.ticketImage) {
          formDataToSend.append(`ticketImage_${index}`, row.ticketImage);
        }
      });

      const response = await axiosInstance.post("/api/ta/submit", formDataToSend);

      if (response.data.success) {
        showNotificationMessage("success", "Travel allowance form submitted successfully!");
        setFormData({
          employeeName: "",
          monthYear: "",
          projectName: "",
          costCenter: "",
          designation: "",
          supportingTA: "",
          payment: "",
          location: "",
        });
        setRows([]);
        formRef.current.reset();
        setTimeout(() => navigate("/ta"), 2000);
      } else {
        throw new Error(response.data.message || "Submission failed");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      let errorMsg = "Failed to submit form";
      if (error.response) {
        errorMsg = error.response.data?.message || errorMsg;
          if (error.response.status === 401) {
          errorMsg = "Session expired. Please login again.";
          TokenManager.clearTokens();
          setTimeout(() => navigate("/login"), 2000);
        }
      } else if (error.request) {
        errorMsg = "No response from server. Make sure backend is running on port 3000.";
      }
      showNotificationMessage("error", errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // --------------------- RENDER ---------------------
  return (
    <Layout>
      {/* Notification Popup */}
      {showNotification && (
        <div
          className={`notification-popup notification-${notificationType}`}
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            zIndex: 9999,
            padding: "12px 16px",
            borderRadius: "6px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            minWidth: "280px",
            animation: "slideIn 0.3s ease",
            fontSize: "0.9rem",
            backgroundColor: 
              notificationType === "success" ? "#d4edda" : 
              notificationType === "error" ? "#f8d7da" : 
              "#d1ecf1",
            color: 
              notificationType === "success" ? "#155724" : 
              notificationType === "error" ? "#721c24" : 
              "#0c5460",
            border: 
              notificationType === "success" ? "1px solid #c3e6cb" : 
              notificationType === "error" ? "1px solid #f5c6cb" : 
              "1px solid #bee5eb",
          }}
        >
          <i
            className={`bi ${
              notificationType === "success" ? "bi-check-circle-fill" : 
              notificationType === "error" ? "bi-exclamation-triangle-fill" : 
              "bi-info-circle-fill"
            }`}
            style={{ fontSize: "1.2rem" }}
          ></i>
          <span style={{ flex: 1 }}>{notificationMessage}</span>
          <button
            onClick={() => setShowNotification(false)}
            style={{
              background: "none",
              border: "none",
              fontSize: "1.2rem",
              cursor: "pointer",
              padding: "0 5px",
              color: "inherit",
            }}
          >
            <i className="bi bi-x"></i>
          </button>
        </div>
      )}

      {/* Header with back button */}
      <div className="d-flex align-items-center justify-content-between" style={{ marginBottom: "1rem", marginTop: "0.25rem" }}>
        <div className="d-flex align-items-center">
          <button
            onClick={() => navigate(-1)}
            className="btn btn-link text-dark p-0 me-2"
            style={{ fontSize: "1.8rem", lineHeight: 1, textDecoration: "none" }}
            title="Go back"
          >
            <i className="bi bi-arrow-left"></i>
          </button>
          <h2 style={{ margin: 0, fontSize: "1.8rem", fontWeight: 500, color: "var(--secondary)" }}>
            Travel Allowance Form
          </h2>
        </div>
        <button
          onClick={() => navigate("/ta-list")}
          className="btn btn-primary"
          title="View all TA submissions"
        >
          <i className="bi bi-list me-2"></i>TA List
        </button>
      </div>

      <section className="section" style={{ padding: 0 }}>
        <div className="row" style={{ margin: 0 }}>
          <div className="col-lg-12" style={{ padding: 0 }}>
            <div className="card">
              <div className="card-body">
                <form ref={formRef} noValidate onSubmit={handleSubmit}>
                  {/* Employee Details Section */}
                  <h5 className="mb-3">Employee Details</h5>
                  <div className="row g-3 mb-4">
                    <div className="col-md-3">
                      <label htmlFor="employeeName" className="form-label">
                        Name <span style={{ color: "#dc3545" }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="employeeName"
                        className={`form-control form-control-sm ${validationErrors.employeeName ? "is-invalid" : ""}`}
                        id="employeeName"
                        value={formData.employeeName}
                        onChange={handleInputChange}
                      />
                      {validationErrors.employeeName && (
                        <div className="invalid-feedback">{validationErrors.employeeName}</div>
                      )}
                    </div>
                    <div className="col-md-3">
                      <label htmlFor="monthYear" className="form-label">
                        Month/Year <span style={{ color: "#dc3545" }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="monthYear"
                        className={`form-control form-control-sm ${validationErrors.monthYear ? "is-invalid" : ""}`}
                        id="monthYear"
                        placeholder="e.g. Jan,2026"
                        value={formData.monthYear}
                        onChange={handleInputChange}
                      />
                      {validationErrors.monthYear && (
                        <div className="invalid-feedback">{validationErrors.monthYear}</div>
                      )}
                    </div>
                    <div className="col-md-3">
                      <label htmlFor="projectName" className="form-label">Project Name</label>
                      <input
                        type="text"
                        name="projectName"
                        className="form-control form-control-sm"
                        id="projectName"
                        value={formData.projectName}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-md-3">
                      <label htmlFor="costCenter" className="form-label">Cost Center</label>
                      <input
                        type="text"
                        name="costCenter"
                        className="form-control form-control-sm"
                        id="costCenter"
                        value={formData.costCenter}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-md-3">
                      <label htmlFor="designation" className="form-label">
                        Designation <span style={{ color: "#dc3545" }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="designation"
                        className={`form-control form-control-sm ${validationErrors.designation ? "is-invalid" : ""}`}
                        id="designation"
                        value={formData.designation}
                        onChange={handleInputChange}
                      />
                      {validationErrors.designation && (
                        <div className="invalid-feedback">{validationErrors.designation}</div>
                      )}
                    </div>
                    <div className="col-md-3">
                      <label htmlFor="supportingTA" className="form-label">Supporting TA</label>
                      <input
                        type="text"
                        name="supportingTA"
                        className="form-control form-control-sm"
                        id="supportingTA"
                        value={formData.supportingTA}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-md-3">
                      <label htmlFor="payment" className="form-label">Payment</label>
                      <input
                        type="text"
                        name="payment"
                        className="form-control form-control-sm"
                        id="payment"
                        value={formData.payment}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-md-3">
                      <label htmlFor="location" className="form-label">Location</label>
                      <input
                        type="text"
                        name="location"
                        className="form-control form-control-sm"
                        id="location"
                        value={formData.location}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  {/* Expense Rows Section */}
                  <h5 className="mb-3">Expense Details</h5>
                  {validationErrors.rows && (
                    <div className="alert alert-danger py-2">{validationErrors.rows}</div>
                  )}

                  {/* Table Header */}
                  <div className="d-flex bg-light fw-bold p-2 mb-2 border rounded" style={{ fontSize: "0.9rem" }}>
                    <div style={{ width: "5%" }}>#</div>
                    <div style={{ width: "10%" }}>Date *</div>
                    <div style={{ width: "10%" }}>Cost Center</div>
                    <div style={{ width: "30%" }}>Particulars</div>
                    <div style={{ width: "10%" }}>Mode</div>
                    <div style={{ width: "10%" }}>Amount *</div>
                    <div style={{ width: "15%" }}>Ticket Image</div>
                    <div style={{ width: "5%" }}></div>
                  </div>

                  {/* Rows */}
                  {rows.map((row, index) => (
                    <div key={row.id} className="d-flex align-items-center gap-2 mb-2 p-1 border-bottom">
                      <div style={{ width: "5%" }}>{index + 1}</div>
                      <div style={{ width: "10%" }}>
                        <input
                          type="date"
                          className={`form-control form-control-sm ${validationErrors[`row_${row.id}_date`] ? "is-invalid" : ""}`}
                          value={row.date}
                          onChange={(e) => updateRow(row.id, "date", e.target.value)}
                        />
                        {validationErrors[`row_${row.id}_date`] && (
                          <div className="invalid-feedback d-block" style={{ fontSize: "0.7rem" }}>
                            {validationErrors[`row_${row.id}_date`]}
                          </div>
                        )}
                      </div>
                      <div style={{ width: "10%" }}>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Cost Center"
                          value={row.costCenter}
                          onChange={(e) => updateRow(row.id, "costCenter", e.target.value)}
                        />
                      </div>
                      <div style={{ width: "30%" }}>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="e.g. Office to Site"
                          value={row.particulars}
                          onChange={(e) => updateRow(row.id, "particulars", e.target.value)}
                        />
                      </div>
                      <div style={{ width: "10%" }}>
                        <select
                          className="form-select form-select-sm"
                          value={row.mode}
                          onChange={(e) => updateRow(row.id, "mode", e.target.value)}
                        >
                          <option value="Cash">Cash</option>
                          <option value="Upi">Upi</option>
                          <option value="Card">Card</option>
                        </select>
                      </div>
                      <div style={{ width: "10%" }}>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className={`form-control form-control-sm ${validationErrors[`row_${row.id}_amount`] ? "is-invalid" : ""}`}
                          placeholder="Amount"
                          value={row.amount}
                          onChange={(e) => updateRow(row.id, "amount", e.target.value)}
                        />
                        {validationErrors[`row_${row.id}_amount`] && (
                          <div className="invalid-feedback d-block" style={{ fontSize: "0.7rem" }}>
                            {validationErrors[`row_${row.id}_amount`]}
                          </div>
                        )}
                      </div>
                      <div style={{ width: "15%" }}>
                        <input
                          type="file"
                          accept="image/*"
                          className="form-control form-control-sm"
                          onChange={(e) => handleTicketImageUpload(row.id, e.target.files[0])}
                        />
                        {row.ticketImage && (
                          <small className="text-success">✓ {row.ticketImage.name}</small>
                        )}
                      </div>
                      <div style={{ width: "5%" }}>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => deleteRow(row.id)}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Add Row Button */}
                  <div className="mt-2 mb-4">
                    <button type="button" className="btn btn-sm btn-success" onClick={addRow}>
                      <i className="bi bi-plus-circle me-1"></i>Add Expense Row
                    </button>
                  </div>

                  {/* Action Buttons */}
                  <div className="d-flex justify-content-end gap-2 mt-3 flex-wrap">
                    <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>
                      Cancel
                    </button>
                    {/* <button type="button" className="btn btn-info" onClick={downloadExcel} title="Download as Excel">
                      <i className="bi bi-file-excel me-1"></i>Excel
                    </button> */}
                    <button type="button" className="btn btn-danger" onClick={downloadPDF} title="Download as PDF">
                      <i className="bi bi-file-pdf me-1"></i>PDF
                    </button>
                    <button type="submit" className="btn btn-success" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          Submitting...
                        </>
                      ) : (
                        "Submit Form"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TravelAllowanceForm;